import { takeLatest, takeEvery, all } from 'redux-saga/effects';
import API from '../services/Api';
import { AccessLevelsTypes } from '../redux/AccessLevelsRedux';
import { AjaxTypes } from '../redux/AjaxRedux';
import { AuditLogsTypes } from '../redux/AuditLogsRedux';
import { AuthTypes } from '../redux/AuthRedux';
import { ControllersTypes } from '../redux/ControllersRedux';
import { CustomersTypes } from '../redux/CustomersRedux';
import { DoorControlsTypes } from '../redux/DoorControlsRedux';
import { DistributorsTypes } from '../redux/DistributorsRedux';
import { EventReportsTypes } from '../redux/EventReportsRedux';
import { OpenDoorTypes } from '../redux/OpenDoorRedux';
import { PersonCountTypes } from '../redux/PersonCountRedux';
import { PersonsTypes } from '../redux/PersonsRedux';
import { SchedulesTypes } from '../redux/SchedulesRedux';
import { SitesTypes } from '../redux/SitesRedux';
import { TagsTypes } from '../redux/TagsRedux';
import { TagTypesTypes } from '../redux/TagTypesRedux';
import { UsersTypes } from '../redux/UsersRedux';

import {
  getAccessLevels,
  createAccessLevel,
  editAccessLevel,
  deleteAccessLevel,
  getAccessLevelControllers,
  getMultipleAccessLevelControllers,
  addControllersToAccessLevel,
  deleteAccessLevelControllers,
} from './AccessLevelsSagas';
import {
  loginAjax,
  getHubs,
  getHubDetails,
  getGroups,
  armHub,
  armGroup,
  getDevices,
  getRooms,
  getLogs,
  getAjaxAccessLevelLinks,
  updateAjaxAccessLevelLinks,
  deleteAjaxAccessLevelLink,
} from './AjaxSagas';
import { getAuditLogs } from './AuditLogsSagas';
import { login, twoFactorAuthenticate, sendResetPasswordEmail, resetPassword } from './AuthSagas';
import {
  getCustomers,
  getCustomersByDistributor,
  getCustomersForAdmin,
  deleteCustomer,
  createCustomer,
  editCustomer,
} from './CustomersSagas';
import {
  getControllers,
  getControllerDetails,
  editController,
  editControllerSettings,
  deleteController,
  uploadFile,
  updateControllers,
  getCustomersForController,
} from './ControllersSagas';
import {
  getDoorControls,
  createDoorControl,
  editDoorControl,
  deleteDoorControl,
  getAvailableDoorControlsControllers,
  getDoorControlControllers,
  addDoorControlControllers,
  editDoorControlControllers,
  deleteDoorControlControllers,
} from './DoorControlsSagas';
import { getDistributorInfo, getDistributorsList } from './DistributorsSagas';
import { openDoor, checkDoorStatus } from './OpenDoorSagas';
import { getPersonCount } from './PersonCountSagas';
import { getPersons, createPerson, editPerson, deletePerson } from './PersonsSagas';
import { getSchedules, createSchedule, editSchedule, deleteSchedule } from './SchedulesSagas';
import {
  getSites,
  getAllSites,
  createSite,
  editSite,
  deleteSite,
  getSiteControllers,
  editSiteController,
  deleteSiteController,
  getSiteAvailableControllers,
  getCustomerControllers,
  addControllerToSite,
} from './SitesSagas';
import { getPersonTags, createPersonTag, editPersonTag, deletePersonTag, activateTag } from './TagsSagas';
import { getTagTypes, createTagType, editTagType, deleteTagType } from './TagTypesSagas';
import {
  getReportModels,
  createReportModel,
  editReportModel,
  deleteReportModel,
  getEventReport,
  getEventReportForExcel,
} from './EventReportsSagas';
import { getUsers, createUser, editUser, deleteUser } from './UsersSagas';

const api = API.createApi();

export default function* rootSaga() {
  yield all([
    takeLatest(AccessLevelsTypes.GET_ACCESS_LEVELS_REQUEST, getAccessLevels, api),
    takeLatest(AccessLevelsTypes.CREATE_ACCESS_LEVEL_REQUEST, createAccessLevel, api),
    takeLatest(AccessLevelsTypes.EDIT_ACCESS_LEVEL_REQUEST, editAccessLevel, api),
    takeLatest(AccessLevelsTypes.DELETE_ACCESS_LEVEL_REQUEST, deleteAccessLevel, api),
    takeLatest(AccessLevelsTypes.GET_ACCESS_LEVEL_CONTROLLERS_REQUEST, getAccessLevelControllers, api),
    takeLatest(AccessLevelsTypes.GET_MULTIPLE_ACCESS_LEVEL_CONTROLLERS_REQUEST, getMultipleAccessLevelControllers, api),
    takeLatest(AccessLevelsTypes.ADD_CONTROLLERS_TO_ACCESS_LEVEL_REQUEST, addControllersToAccessLevel, api),
    takeLatest(AccessLevelsTypes.DELETE_ACCESS_LEVEL_CONTROLLERS_REQUEST, deleteAccessLevelControllers, api),

    takeLatest(AuditLogsTypes.GET_AUDIT_LOGS_REQUEST, getAuditLogs, api),

    takeLatest(AjaxTypes.LOGIN_AJAX_REQUEST, loginAjax, api),
    takeLatest(AjaxTypes.GET_HUBS_REQUEST, getHubs, api),
    takeEvery(AjaxTypes.GET_HUB_DETAILS_REQUEST, getHubDetails, api),
    takeEvery(AjaxTypes.GET_GROUPS_REQUEST, getGroups, api),
    takeLatest(AjaxTypes.ARM_HUB_REQUEST, armHub, api),
    takeLatest(AjaxTypes.ARM_GROUP_REQUEST, armGroup, api),
    takeLatest(AjaxTypes.GET_DEVICES_REQUEST, getDevices, api),
    takeLatest(AjaxTypes.GET_ROOMS_REQUEST, getRooms, api),
    takeEvery(AjaxTypes.GET_LOGS_REQUEST, getLogs, api),
    takeEvery(AjaxTypes.GET_AJAX_ACCESS_LEVEL_LINKS_REQUEST, getAjaxAccessLevelLinks, api),
    takeEvery(AjaxTypes.UPDATE_AJAX_ACCESS_LEVEL_LINKS_REQUEST, updateAjaxAccessLevelLinks, api),
    takeEvery(AjaxTypes.DELETE_AJAX_ACCESS_LEVEL_LINK_REQUEST, deleteAjaxAccessLevelLink, api),

    takeLatest(AuthTypes.LOGIN_REQUEST, login, api),
    takeLatest(AuthTypes.TWO_FACTOR_AUTHENTICATE_REQUEST, twoFactorAuthenticate, api),
    takeLatest(AuthTypes.SEND_RESET_PASSWORD_EMAIL_REQUEST, sendResetPasswordEmail, api),
    takeLatest(AuthTypes.RESET_PASSWORD_REQUEST, resetPassword, api),

    takeLatest(ControllersTypes.GET_CONTROLLERS_REQUEST, getControllers, api),
    takeLatest(ControllersTypes.GET_CONTROLLER_DETAILS_REQUEST, getControllerDetails, api),
    takeLatest(ControllersTypes.EDIT_CONTROLLER_REQUEST, editController, api),
    takeLatest(ControllersTypes.EDIT_CONTROLLER_SETTINGS_REQUEST, editControllerSettings, api),
    takeLatest(ControllersTypes.DELETE_CONTROLLER_REQUEST, deleteController, api),
    takeLatest(ControllersTypes.UPLOAD_FILE_REQUEST, uploadFile, api),
    takeLatest(ControllersTypes.UPDATE_CONTROLLERS_REQUEST, updateControllers, api),
    takeLatest(ControllersTypes.GET_CUSTOMERS_FOR_CONTROLLER_REQUEST, getCustomersForController, api),

    takeLatest(CustomersTypes.CUSTOMERS_REQUEST, getCustomers, api),
    takeLatest(CustomersTypes.GET_CUSTOMERS_BY_DISTRIBUTOR_REQUEST, getCustomersByDistributor, api),
    takeLatest(CustomersTypes.GET_CUSTOMERS_FOR_ADMIN_REQUEST, getCustomersForAdmin, api),
    takeLatest(CustomersTypes.DELETE_CUSTOMER_REQUEST, deleteCustomer, api),
    takeLatest(CustomersTypes.CREATE_CUSTOMER_REQUEST, createCustomer, api),
    takeLatest(CustomersTypes.EDIT_CUSTOMER_REQUEST, editCustomer, api),

    takeLatest(DistributorsTypes.GET_DISTRIBUTORS_LIST_REQUEST, getDistributorsList, api),
    takeLatest(DistributorsTypes.GET_DISTRIBUTOR_INFO_REQUEST, getDistributorInfo, api),

    takeLatest(DoorControlsTypes.GET_DOOR_CONTROLS_REQUEST, getDoorControls, api),
    takeLatest(DoorControlsTypes.CREATE_DOOR_CONTROL_REQUEST, createDoorControl, api),
    takeLatest(DoorControlsTypes.EDIT_DOOR_CONTROL_REQUEST, editDoorControl, api),
    takeLatest(DoorControlsTypes.DELETE_DOOR_CONTROL_REQUEST, deleteDoorControl, api),
    takeLatest(DoorControlsTypes.GET_AVAILABLE_CONTROLLERS_REQUEST, getAvailableDoorControlsControllers, api),
    takeLatest(DoorControlsTypes.GET_DOOR_CONTROL_CONTROLLERS_REQUEST, getDoorControlControllers, api),
    takeLatest(DoorControlsTypes.ADD_DOOR_CONTROL_CONTROLLERS_REQUEST, addDoorControlControllers, api),
    takeLatest(DoorControlsTypes.EDIT_DOOR_CONTROL_CONTROLLERS_REQUEST, editDoorControlControllers, api),
    takeLatest(DoorControlsTypes.DELETE_DOOR_CONTROL_CONTROLLERS_REQUEST, deleteDoorControlControllers, api),

    takeLatest(EventReportsTypes.GET_REPORT_MODELS_REQUEST, getReportModels, api),
    takeLatest(EventReportsTypes.CREATE_REPORT_MODEL_REQUEST, createReportModel, api),
    takeLatest(EventReportsTypes.EDIT_REPORT_MODEL_REQUEST, editReportModel, api),
    takeLatest(EventReportsTypes.DELETE_REPORT_MODEL_REQUEST, deleteReportModel, api),
    takeLatest(EventReportsTypes.GET_EVENT_REPORT_REQUEST, getEventReport, api),
    takeLatest(EventReportsTypes.GET_EVENT_REPORT_FOR_EXCEL, getEventReportForExcel, api),

    takeLatest(OpenDoorTypes.OPEN_DOOR_REQUEST, openDoor, api),
    takeEvery(OpenDoorTypes.CHECK_DOOR_STATUS_REQUEST, checkDoorStatus, api),

    takeLatest(PersonCountTypes.GET_PERSON_COUNT_REQUEST, getPersonCount, api),

    takeLatest(PersonsTypes.GET_PERSONS_REQUEST, getPersons, api),
    takeLatest(PersonsTypes.CREATE_PERSON_REQUEST, createPerson, api),
    takeLatest(PersonsTypes.EDIT_PERSON_REQUEST, editPerson, api),
    takeLatest(PersonsTypes.DELETE_PERSON_REQUEST, deletePerson, api),

    takeLatest(SchedulesTypes.GET_SCHEDULES_REQUEST, getSchedules, api),
    takeLatest(SchedulesTypes.CREATE_SCHEDULE_REQUEST, createSchedule, api),
    takeLatest(SchedulesTypes.EDIT_SCHEDULE_REQUEST, editSchedule, api),
    takeLatest(SchedulesTypes.DELETE_SCHEDULE_REQUEST, deleteSchedule, api),

    takeLatest(SitesTypes.GET_SITES_REQUEST, getSites, api),
    takeLatest(SitesTypes.CREATE_SITE_REQUEST, createSite, api),
    takeLatest(SitesTypes.EDIT_SITE_REQUEST, editSite, api),
    takeLatest(SitesTypes.DELETE_SITE_REQUEST, deleteSite, api),
    takeLatest(SitesTypes.GET_ALL_SITES_REQUEST, getAllSites, api),
    takeLatest(SitesTypes.GET_SITE_CONTROLLERS_REQUEST, getSiteControllers, api),
    takeLatest(SitesTypes.EDIT_SITE_CONTROLLER_REQUEST, editSiteController, api),
    takeLatest(SitesTypes.DELETE_SITE_CONTROLLER_REQUEST, deleteSiteController, api),
    takeLatest(SitesTypes.GET_SITE_AVAILABLE_CONTROLLERS_REQUEST, getSiteAvailableControllers, api),
    takeLatest(SitesTypes.GET_CUSTOMER_CONTROLLERS_REQUEST, getCustomerControllers, api),
    takeLatest(SitesTypes.ADD_CONTROLLER_TO_SITE_REQUEST, addControllerToSite, api),

    takeLatest(TagsTypes.GET_PERSON_TAGS_REQUEST, getPersonTags, api),
    takeLatest(TagsTypes.CREATE_PERSON_TAG_REQUEST, createPersonTag, api),
    takeLatest(TagsTypes.EDIT_PERSON_TAG_REQUEST, editPersonTag, api),
    takeLatest(TagsTypes.DELETE_PERSON_TAG_REQUEST, deletePersonTag, api),
    takeLatest(TagsTypes.ACTIVATE_TAG_REQUEST, activateTag, api),

    takeLatest(TagTypesTypes.GET_TAG_TYPES_REQUEST, getTagTypes, api),
    takeLatest(TagTypesTypes.CREATE_TAG_TYPE_REQUEST, createTagType, api),
    takeLatest(TagTypesTypes.EDIT_TAG_TYPE_REQUEST, editTagType, api),
    takeLatest(TagTypesTypes.DELETE_TAG_TYPE_REQUEST, deleteTagType, api),

    takeLatest(UsersTypes.GET_USERS_REQUEST, getUsers, api),
    takeLatest(UsersTypes.CREATE_USER_REQUEST, createUser, api),
    takeLatest(UsersTypes.EDIT_USER_REQUEST, editUser, api),
    takeLatest(UsersTypes.DELETE_USER_REQUEST, deleteUser, api),
  ]);
}
